import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CSVDownloader, readString } from 'react-papaparse';
import { ButtonGroup, Text } from '@paygreen/paygreen-ui';
import {
    ModalForm,
    ValidateButton,
    CancelButton,
    Loader,
    Message,
} from '../../components';
import { useUser } from '../../context';
import { formatDateApi } from '../../utils/tools';
import { getDonationsExport } from './service';
import ExportDonationsForm from './ExportDonationsForm';
import ExportDonationsFormMessage from './ExportDonationsFormMessage';

const ExportDonationsModal = ({
    t,
    setOpen,
    isOpen,
    startDate: donationsStartDate,
    endDate: donationsEndDate,
}) => {
    const { user } = useUser();

    const [isLoading, setIsLoading] = useState(false);
    const [errorExportMessage, setErrorExportMessage] = useState();
    const [donationsExportData, setDonationsExportData] = useState(null);
    const [startDate, setStartDate] = useState(donationsStartDate);
    const [endDate, setEndDate] = useState(donationsEndDate);

    const finalRole = user?.role === 'ASSOCIATION' ? 'ASSOCIATION' : 'SHOP';

    // To update dates dynamically if dates are changed for donations table
    useEffect(() => {
        setStartDate(donationsStartDate);
        setEndDate(donationsEndDate);
    }, [donationsStartDate, donationsEndDate]);

    /**
     * GET request to get donations date on specific dates
     * @returns {object} - object with donations data
     */
    const getExports = () => {
        const params = {
            begin: formatDateApi(startDate),
            end: formatDateApi(endDate),
        };

        setIsLoading(true);

        getDonationsExport(params)
            .then((res) => {
                toast(
                    <Text colorPallet="theme" textSize="xs">
                        {t('form.toastSuccess.donationsExport')}
                    </Text>,
                );
                setDonationsExportData(res.data);
                setOpen(false);
                document.getElementsByClassName('csv-download')[0].click();
            })
            .catch((error) => {
                const { detail, title } = error?.response?.data;
                setErrorExportMessage(detail + ' / ' + title);
            })
            .finally(() => setIsLoading(false));
    };

    const cancelCloseModal = () => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            setErrorExportMessage();
            setStartDate(donationsStartDate);
            setEndDate(donationsEndDate);
        }, 500);
    };

    /**
     * @param {string} header - header received in data from api
     * @returns string header cleaned and translated
     */
    const handleCsvHeader = (header) => {
        const cleanedHeader = header
            .toString()
            .toLowerCase()
            .replace(/'|[\s+]/g, '');
        return t(`shopDonations.exports.headers.${finalRole}.${cleanedHeader}`);
    };

    return (
        <ModalForm
            modalTitle={t('shopDonations.exports.modalTitle')}
            blockWidth="md"
            paddingLateral="sm"
            hasCloseCross={true}
            isOpen={isOpen}
            actionOnClose={cancelCloseModal}
        >
            <ExportDonationsForm
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                endDate={endDate}
                startDate={startDate}
                errorMessage={errorExportMessage}
            >
                <ExportDonationsFormMessage endDate={endDate} />

                {errorExportMessage ? (
                    <Message
                        message={errorExportMessage}
                        type="error"
                        marginTop="md"
                        marginBottom="md"
                        paddingLateral="lg"
                    />
                ) : null}

                <ButtonGroup marginTop="sm" marginBottom="sm">
                    <CancelButton
                        buttonSize="md"
                        type="reset"
                        onClick={cancelCloseModal}
                    >
                        {t('form.button.cancel')}
                    </CancelButton>

                    <CSVDownloader
                        filename={`CHARITYKIT_EXPORTS_${startDate}_${endDate}`}
                        data={
                            donationsExportData &&
                            readString(donationsExportData, {
                                header: true,
                                transformHeader: (header) => {
                                    return handleCsvHeader(header);
                                },
                            })
                        }
                        type="button"
                        className="csv-download"
                    />

                    <ValidateButton
                        buttonSize="md"
                        isDisabled={isLoading}
                        icon="DownloadIcon"
                        onClick={getExports}
                    >
                        {t('form.button.export')}
                    </ValidateButton>
                </ButtonGroup>
            </ExportDonationsForm>

            {isLoading && (
                <Loader colorTheme="quaternary" isLoading={isLoading} />
            )}
        </ModalForm>
    );
};

ExportDonationsModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
};

export default withTranslation('common')(ExportDonationsModal);
